import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Linkedin, Youtube, Instagram, Facebook, MessageCircle, Globe2, Search, Menu, X, ShoppingCart } from "lucide-react";

import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { mainNavItems } from "../../mock";
import { useLanguage } from "../../contexts/LanguageContext";
import { useQuoteCart } from "../../contexts/QuoteCartContext";
import { t } from "../../i18n";
import { trackGoogleAdsConversion } from "../../utils/googleAds";
import MegaMenu from "./MegaMenu";

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, toggleLang } = useLanguage();
  const { totalUniqueItems } = useQuoteCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const megaMenuTimeoutRef = useRef(null);

  const handleProductsMouseEnter = () => {
    if (megaMenuTimeoutRef.current) {
      clearTimeout(megaMenuTimeoutRef.current);
      megaMenuTimeoutRef.current = null;
    }
    setMegaMenuOpen(true);
  };

  const handleProductsMouseLeave = () => {
    megaMenuTimeoutRef.current = setTimeout(() => {
      setMegaMenuOpen(false);
    }, 150);
  };
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setMobileOpen(false);
    const params = new URLSearchParams(location.search);
    const query = params.get("q") || "";
    if (location.pathname.startsWith("/search")) {
      setSearchTerm(query);
    } else {
      setSearchTerm("");
    }
  }, [location.pathname, location.search]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
      setMobileOpen(false);
    }
  };

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-40 text-white font-medium">
      {/* Top utility bar */}
      <div className="bg-transparent">
        <div className="mx-auto flex max-w-6xl items-center justify-end px-4 py-2 text-xs sm:text-[13px]">
          <div className="hidden items-center space-x-4 md:flex">
            <div className="flex items-center bg-black/40 px-2.5 h-[30px] rounded-full space-x-2 text-white">
              <a
                href="https://api.whatsapp.com/send?phone=905306140705"
                target="_blank"
                rel="noreferrer"
                onClick={() => trackGoogleAdsConversion('whatsapp_click', { source: 'header_topbar' })}
                className="hover:text-primary transition-colors"
                title="WhatsApp"
              >
                <MessageCircle className="h-[18px] w-[18px]" />
              </a>
              <a
                href="https://www.instagram.com/profinoks/?hl=tr"
                target="_blank"
                rel="noreferrer"
                className="hover:text-primary transition-colors"
                title="Instagram"
              >
                <Instagram className="h-[18px] w-[18px]" />
              </a>
              <a
                href="https://www.facebook.com/profinoks.endustriyelmutfakekipmanlari"
                target="_blank"
                rel="noreferrer"
                className="hover:text-primary transition-colors"
                title="Facebook"
              >
                <Facebook className="h-[18px] w-[18px]" />
              </a>
              <a
                href="https://www.linkedin.com/in/profinoks-end%C3%BCstriyel-mutfak-ekipmanlari-4471921b2/"
                target="_blank"
                rel="noreferrer"
                className="hover:text-primary transition-colors"
                title="LinkedIn"
              >
                <Linkedin className="h-[18px] w-[18px]" />
              </a>
              <a
                href="https://www.youtube.com/channel/UCTsoSgntPEXAshH80VtSa-g/featured"
                target="_blank"
                rel="noreferrer"
                className="hover:text-primary transition-colors"
                title="YouTube"
              >
                <Youtube className="h-[18px] w-[18px]" />
              </a>
            </div>

            <button
              onClick={() => {
                window.open("https://drive.google.com/file/d/1DTSz_N8ljmdiRxP0BQP_TmKiXxGlJwRQ/view?usp=sharing", "_blank");
              }}
              className="bg-black/40 px-3 h-[30px] rounded-full text-white hover:bg-black/60 transition-colors flex items-center"
            >
              <span className="align-middle mr-1 inline-block h-[14px] w-[14px] rounded-sm bg-white/20" />
              <span className="text-[12px] font-semibold">{t(lang, "nav.catalogue")}</span>
            </button>


          </div>

          <div className="ml-4 flex flex-1 items-center justify-end space-x-3">
            <button
              type="button"
              onClick={toggleLang}
              className="bg-black/40 px-3 h-[30px] rounded-full flex items-center space-x-1.5 text-white hover:bg-black/60"
            >
              <Globe2 className="h-[18px] w-[18px]" />
              <span className="text-[11px] font-bold uppercase tracking-[0.1em]">
                {lang === "en" ? "EN / TR" : "TR / EN"}
              </span>
            </button>

            <form
              onSubmit={handleSearchSubmit}
              className="ml-2 hidden max-w-[180px] flex-1 items-center space-x-1 sm:flex"
            >
              <div className="relative w-full">
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-[30px] bg-primary/60 pr-8 text-[12px] placeholder:text-white/70 border-white/20 text-white focus-visible:ring-primary/40"
                  placeholder={t(lang, "search.placeholder")}
                />
                <button
                  type="submit"
                  className="absolute inset-y-0 right-2 flex items-center text-black/70 hover:text-black"
                >
                  <Search className="h-4 w-4 text-white/70 hover:text-white" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="bg-primary/50 backdrop-blur-md border-b border-white/5 shadow-lg">
        <div className="mx-auto flex max-w-6xl items-center justify-start px-4 py-3">
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="/profinoks/logo.png"
              alt="Profinoks Logo"
              className="h-10 w-auto sm:h-12"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/150x50?text=PROFINOKS";
              }}
            />
          </Link>

          <nav className="hidden items-center ml-auto space-x-2.5 text-[12px] xl:space-x-5 xl:text-[14px] font-bold uppercase tracking-[0.08em] lg:flex h-full whitespace-nowrap">
            {mainNavItems.map((item) => (
              <div
                key={item.path}
                className="h-full flex items-center"
                onMouseEnter={() => item.translationKey === "nav.products" && handleProductsMouseEnter()}
                onMouseLeave={() => item.translationKey === "nav.products" && handleProductsMouseLeave()}
              >
                <NavLink
                  to={item.path}
                  className={({ isActive: isNavActive }) =>
                    [
                      "relative pb-1 transition-colors",
                      isNavActive || isActive(item.path)
                        ? "text-white"
                        : "text-white/90 hover:text-white",
                    ].join(" ")
                  }
                >
                  {({ isActive: isNavActive }) => (
                    <>
                      <span>{t(lang, item.translationKey)}</span>
                      <span
                        className={[
                          "absolute inset-x-0 -bottom-0.5 h-0.5 origin-left bg-white transition-transform",
                          isNavActive || isActive(item.path)
                            ? "scale-x-100"
                            : "scale-x-0",
                        ].join(" ")}
                      />
                    </>
                  )}
                </NavLink>
              </div>
            ))}
            
            {/* Desktop Quote Cart Link */}
            <NavLink
              to="/teklif-sepeti"
              className={({ isActive: isNavActive }) =>
                [
                  "pb-1 transition-colors flex items-center space-x-2 text-white/95 hover:text-primary-foreground/90 ml-2.5 xl:ml-5 flex-shrink-0",
                  isNavActive ? "text-primary" : "text-white/95 hover:text-primary"
                ].join(" ")
              }
            >
              <div className="relative">
                <ShoppingCart className="h-[20px] w-[20px]" />
                {totalUniqueItems > 0 && (
                  <span className="absolute -top-2.5 -right-2.5 bg-primary text-white text-[9px] font-bold h-4 w-4 flex items-center justify-center rounded-full animate-pulse">
                    {totalUniqueItems}
                  </span>
                )}
              </div>
              <span className="text-[12px] xl:text-[14px] font-bold">{lang === "tr" ? "SEPETİM" : "CART"}</span>
            </NavLink>
          </nav>

          {/* Mega Menu Overlay */}
          {megaMenuOpen && (
            <MegaMenu
              lang={lang}
              onMouseEnter={handleProductsMouseEnter}
              onMouseLeave={handleProductsMouseLeave}
              onClose={() => setMegaMenuOpen(false)}
            />
          )}

          <div className="flex items-center ml-auto space-x-3 lg:hidden">
            <button
              type="button"
              onClick={() => setMobileOpen((open) => !open)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-white hover:border-white"
              aria-label="Toggle navigation"
            >
              {mobileOpen ? (
                <X className="h-4 w-4" />
              ) : (
                <Menu className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="border-t border-white/10 bg-black/95 lg:hidden">
            <div className="mx-auto max-w-6xl px-4 pb-4 pt-2">
              <form onSubmit={handleSearchSubmit} className="mb-3 flex items-center space-x-2">
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-9 flex-1 bg-white/10 text-xs placeholder:text-white/50 border-white/20 focus-visible:ring-white/40"
                  placeholder={t(lang, "search.placeholder")}
                />
                <Button
                  type="submit"
                  size="icon"
                  className="h-9 w-9 bg-primary text-white hover:bg-primary/80"
                >
                  <Search className="h-4 w-4" />
                </Button>
              </form>

              <nav className="flex flex-col space-y-2 text-xs font-medium uppercase tracking-[0.16em]">
                {mainNavItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive: isNavActive }) =>
                      [
                        "flex items-center justify-between rounded-sm px-2 py-1.5",
                        isNavActive || isActive(item.path)
                          ? "bg-white/10 text-white"
                          : "text-white/90 hover:bg-white/5",
                      ].join(" ")
                    }
                  >
                    {t(lang, item.translationKey)}
                  </NavLink>
                ))}
                
                {/* Mobile Quote Cart Link */}
                <NavLink
                  to="/teklif-sepeti"
                  className={({ isActive: isNavActive }) =>
                    [
                      "flex items-center justify-between rounded-sm px-2 py-2 border-t border-white/10 mt-2 pt-2",
                      isNavActive
                        ? "bg-white/10 text-white font-bold"
                        : "text-white/90 hover:bg-white/5",
                    ].join(" ")
                  }
                >
                  <span className="flex items-center space-x-2">
                    <ShoppingCart className="h-4 w-4 text-primary" />
                    <span>{lang === "tr" ? "TEKLİF SEPETİ" : "QUOTE CART"}</span>
                  </span>
                  {totalUniqueItems > 0 && (
                    <span className="bg-primary text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {totalUniqueItems}
                    </span>
                  )}
                </NavLink>

                {/* Mobile Language Switcher */}
                <button
                  type="button"
                  onClick={toggleLang}
                  className="flex items-center justify-between rounded-sm px-2 py-2 border-t border-white/10 mt-1 pt-2 text-white/90 hover:bg-white/5"
                >
                  <span className="flex items-center space-x-2">
                    <Globe2 className="h-4 w-4 text-primary" />
                    <span>{lang === "en" ? "DİL: EN (TÜRKÇE'YE GEÇ)" : "DİL: TR (SWITCH TO ENGLISH)"}</span>
                  </span>
                  <span className="bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {lang === "en" ? "EN" : "TR"}
                  </span>
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
